import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-password-strength-indicator',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  templateUrl: './password-strength-indicator.component.html',
  styleUrl: './password-strength-indicator.component.scss'
})
export class PasswordStrengthIndicatorComponent implements OnInit, OnDestroy {
  @Input() control!: AbstractControl;
  @Input() translationPrefix: string = 'register';
  @Input() showTitle: boolean = true;

  private destroy$ = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Subscribe to password changes for real-time validation
    if (this.control) {
      this.control.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.cdr.detectChanges();
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Password validation methods
  hasPasswordValue(): boolean {
    return !!this.control?.value;
  }

  isMinLengthValid(): boolean {
    const password = this.control?.value || '';
    return password.length >= 8;
  }

  hasUppercase(): boolean {
    const password = this.control?.value || '';
    return /[A-Z]/.test(password);
  }

  hasLowercase(): boolean {
    const password = this.control?.value || '';
    return /[a-z]/.test(password);
  }

  hasNumber(): boolean {
    const password = this.control?.value || '';
    return /[0-9]/.test(password);
  }

  hasSpecialChar(): boolean {
    const password = this.control?.value || '';
    return /[!@#$%^&*(),.?":{}|<>]/.test(password);
  }

  // Validation state methods
  getValidationIcon(validationFn: () => boolean): string {
    if (!this.hasPasswordValue()) {
      return 'pi-circle text-greyscale-400';
    }
    
    // Check individual validation result in real-time
    if (validationFn()) {
      return 'pi-check text-validation-600';
    }
    
    return 'pi-times text-error-500';
  }
  
  getValidationTextClass(validationFn: () => boolean): string {
    if (!this.hasPasswordValue()) {
      return 'text-greyscale-500';
    }
    
    // Check individual validation result in real-time
    if (validationFn()) {
      return 'text-validation-600';
    }
    
    return 'text-error-500';
  }

  // Translation key helpers
  getTitleTranslationKey(): string {
    if (this.translationPrefix === 'resetPasswordPage') {
      return 'resetPasswordPage.pickPassword';
    }
    return 'register.passwordStrength';
  }

  getLengthTranslationKey(): string {
    if (this.translationPrefix === 'resetPasswordPage') {
      return 'resetPasswordPage.length';
    }
    return 'register.minEightChars';
  }

  getUppercaseTranslationKey(): string {
    if (this.translationPrefix === 'resetPasswordPage') {
      return 'resetPasswordPage.uppercase';
    }
    return 'register.oneUppercase';
  }

  getLowercaseTranslationKey(): string {
    if (this.translationPrefix === 'resetPasswordPage') {
      return 'resetPasswordPage.lowercase';
    }
    return 'register.oneLowercase';
  }

  getNumericTranslationKey(): string {
    if (this.translationPrefix === 'resetPasswordPage') {
      return 'resetPasswordPage.numeric';
    }
    return 'register.oneNumber';
  }

  getSpecialTranslationKey(): string {
    if (this.translationPrefix === 'resetPasswordPage') {
      return 'resetPasswordPage.special';
    }
    return 'register.oneSpecialChar';
  }
}